import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import { FormTransformers } from '@/common/utils/formTransformers'
import { CompanyData } from '@/features/companies/types/companies.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { options as usageDeclarationModesOptions } from '@/features/privateCompanies/constants/usageDeclarationModes.constant'
import { options as createUsageDeclarationModesOptions } from '@/features/privateCompanies/constants/createUsageDeclarationMode.constant'
import { useUpdateGeneralPolitics } from '@/features/privateCompanies/hooks/politics/useUpdateGeneralPolitics'
import { updateGeneralPoliticsSchema, UpdateGeneralPoliticsSchema } from '@/features/privateCompanies/schemas/politics/updateGeneralPolitics.schema'

interface UpdateUsageDeclarationModeModalProps {
  company?: CompanyData
}

export const UpdateUsageDeclarationModeModal = ({ company }: UpdateUsageDeclarationModeModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<UpdateGeneralPoliticsSchema>({
    resolver: zodResolver(updateGeneralPoliticsSchema),
  })

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['company', company?.id] })
    setIsOpen(false)
  }

  const { mutate: updateGeneralPolitics, isPending: updateGeneralPoliticsIsPending } = useUpdateGeneralPolitics({
    companyId: company?.id,
    onSuccess,
    onError: FormTransformers.errorsTransformer<UpdateGeneralPoliticsSchema>({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<UpdateGeneralPoliticsSchema> = (data) => {
    updateGeneralPolitics(data)
  }

  useEffect(() => {
    if (isOpen) {
      reset({
        company: {
          usage_declaration_mode: usageDeclarationModesOptions.find(
            (opition) => opition.value === company?.usage_declaration_mode
          ),
          create_usage_declaration_mode: createUsageDeclarationModesOptions.find(
            (opition) => opition.value === company?.create_usage_declaration_mode
          ),
        }
      })
    }
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button
          name="open_update_usage_declaration_mode_modal"
          operationKlassToCheckPermission="Admin::Client::Companies::UpdatePoliticsGeneralInformation"
          className="h-[2.125rem] text-sm text-white-800"
        >
          Editar
        </Button>
      }
      title="Editando declaração de uso"
      dataTestid="update_usage_declaration_mode"
      useCancellableFooter
      confirmButtonClassName="bg-blue-700"
      disabled={updateGeneralPoliticsIsPending}
      confirmButtonIsLoading={updateGeneralPoliticsIsPending}
      body={
        <>
          <form className="flex flex-col gap-y-4 text-white-800">
            <SelectController
              control={control}
              name="company.usage_declaration_mode"
              label="Declaração de uso"
              errorMessage={errors?.company?.usage_declaration_mode?.message}
              options={usageDeclarationModesOptions}
              isDisabled={updateGeneralPoliticsIsPending}
              markAsRequired
            />

            <SelectController
              control={control}
              name="company.create_usage_declaration_mode"
              label="Modo de alteração de quilometragem"
              errorMessage={errors?.company?.create_usage_declaration_mode?.message}
              options={createUsageDeclarationModesOptions}
              isDisabled={updateGeneralPoliticsIsPending}
              markAsRequired
            />
          </form>

          {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
        </>
      }
    />
  )
}
